/**
 * Narrative content for the engine-reliability study: the mechanisms, the
 * architecture split, and the operator console.
 *
 * Every figure here is a production measurement or a shipped configuration
 * value read out of the engine's own source. Two things are deliberate:
 *
 * 1. Vendors are never named. Prose says "one provider"; the explorer's own
 *    table numbers them (see components/shared/sanitized-note.tsx).
 * 2. These figures come from the engine's internal audits and config, NOT
 *    from the 962,376-generation dataset the explorer scores. The method
 *    footer states that separation so the page never implies one dataset.
 */

export type Mechanism = {
  id: string;
  name: string;
  /** Compact value chip shown on the closed row. */
  win: string;
  /** The lead sentence in the open panel. */
  analogy: string;
  paragraphs: string[];
};

export const MECHANISMS: Mechanism[] = [
  {
    id: 'circuit-breaker',
    name: 'A provider having a bad hour stops getting traffic, and nothing has to remember that',
    win: '3 fails / 60s · no state store',
    analogy:
      'Instead of posting a guard at the door to remember who has been causing trouble, I read the last sixty seconds of the visitor log every time someone knocks.',
    paragraphs: [
      'Three failures for a model on one provider inside sixty seconds, and that pairing is skipped. Nothing stores the verdict: every check recounts the attempt log, so there is no timer to reset and nothing to lose on restart. Bad prompts never count toward it, and a single-provider model skips the check entirely.',
    ],
  },
  {
    id: 'fallback-chain',
    name: 'When one provider fails, the same model comes from another and the user never learns which',
    win: 'up to 3 providers · 0s between hops',
    analogy:
      'A rebooking desk with a printed list of partner airlines: you only ever move down the list, and the desk checks the next airline actually flies your route before booking you.',
    paragraphs: [
      'Each model carries an ordered provider list and the walk only moves forward: retries inside a provider wait five, ten, then fifteen seconds; hopping to the next provider waits zero. A prompt-caused failure stops the chain immediately, and the whole walk is capped at twenty five minutes.',
    ],
  },
  {
    id: 'watchdog',
    name: 'I stopped throwing away work that was already paid for',
    win: '165 of 226 "timeouts" had finished',
    analogy:
      'Before cancelling a late delivery, the app phones the restaurant. Often the food is already on the counter, and cancelling means paying for the same meal twice.',
    paragraphs: [
      'Each attempt carries two clocks: one to go ask the provider what is happening, one absolute ceiling. The rule came from an audit, and late results are still delivered for up to forty eight hours with the refund left in place.',
    ],
  },
  {
    id: 'classification',
    name: 'Telling a bad prompt apart from a bad provider is what makes the number honest',
    win: '8 categories · 127 stop-patterns',
    analogy:
      'A returns counter that reads the reason written on the box: most phrases mean this can never be fixed, and only a few mean try again later.',
    paragraphs: [
      "Eight categories decide whether the chain continues, what the user is told, and whether that provider's health takes the hit. The first version matched one word; it is now 127 phrases, each pinned to the production failure that forced it.",
    ],
  },
];

export type ArchitectureCard = { title: string; body: string };

export const ARCHITECTURE_CARDS: ArchitectureCard[] = [
  {
    title: 'Mechanics, not policy',
    body: 'Billing, credits and refunds stayed behind, so the engine can be redeployed mid-incident.',
  },
  {
    title: 'Stateless',
    body: 'No cache, no KV: state is Postgres. The only memory is a five second health note.',
  },
  {
    title: 'Never touches money',
    body: 'One signed callback with five outcomes; the refund decision lives on the other side.',
  },
  {
    title: 'Failover is data',
    body: 'Provider order, timeouts and retries are rows edited in the console. No deploy.',
  },
];

/** The operator console, one openable row with the live tape beneath. */
export const CONSOLE: Mechanism = {
  id: 'console',
  name: 'Five screens replaced the debug page',
  win: '5 screens · no metrics store',
  analogy:
    'The tape is a heart monitor with a fixed-width trace: an hour or a month, always sixty ticks across, so your eye learns one shape and spots the wrong one.',
  paragraphs: [
    'No metrics store: every number is a live query against the tables the engine writes, polled per panel. Not read-only either: acknowledge and resolve are separate states, routing is edited with save-time bounds, and one keystroke fires a test generation.',
  ],
};
